import { useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import TextField from '@mui/material/TextField';

const QuarryPitMap = (props) => {
  const { query, customerQuery, handleMaps } = props;
  const mapRef = useRef(null);
  const methods = useFormContext();
  const { setValue, watch } = methods;

  const [map, setMap] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const marker = useRef(null);
  const latitude = watch('l_latitude');
  const longitude = watch('l_longitude');

  useEffect(() => {
    if (
      latitude === undefined ||
      latitude === null ||
      latitude === '' ||
      longitude === undefined ||
      longitude === null ||
      longitude === ''
    ) {
      setSearchValue('');
      return;
    }

    const lat = Number(latitude);
    const lng = Number(longitude);

    // Default form values are 0,0 — keep the search field empty until real coords exist
    if (Number.isNaN(lat) || Number.isNaN(lng) || (lat === 0 && lng === 0)) {
      setSearchValue('');
      return;
    }

    setSearchValue(`${latitude}, ${longitude}`);
  }, [latitude, longitude]);

  useEffect(() => {
    if (!map) {
      return;
    }

    const lat = Number(latitude);
    const lng = Number(longitude);

    if (
      latitude === undefined ||
      latitude === null ||
      latitude === '' ||
      longitude === undefined ||
      longitude === null ||
      longitude === '' ||
      Number.isNaN(lat) ||
      Number.isNaN(lng) ||
      (lat === 0 && lng === 0)
    ) {
      return;
    }

    const position = { lat, lng };
    changeMarkerPosition(position);
    map.setCenter(position);
    map.panTo(position);
  }, [latitude, longitude, map]);

  const checkLongName = (objData, id) => {
    if (
      objData &&
      objData.address_components &&
      objData.address_components.length &&
      objData.address_components[id] != undefined &&
      objData.address_components.length == id &&
      objData.address_components[id].long_name
    ) {
      return true;
    } else {
      return false;
    }
  };

  useEffect(() => {
    try {
      const newMap = new window.google.maps.Map(mapRef.current, {
        zoom: 12,
        center: { lat: 30.2426319, lng: -97.7542349 }, // Initial center
      });
      setMap(newMap);
    } catch (error) {
      console.error('Error initializing Google Map:', error);
    }
  }, [mapRef.current]);

  useEffect(() => {
    if (map) {
      try {
        // Add event listener for map click
        map.addListener('click', onMapClick);
      } catch (error) {
        console.error('Error adding map click listener:', error);
      }
    }
  }, [map]);

  const onMapClick = (event) => {
    try {
      const clickedLocation = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      };

      setValue('l_latitude', event.latLng.lat());
      setValue('l_longitude', event.latLng.lng());

      changeMarkerPosition(clickedLocation);
      map.panTo(clickedLocation);

      handleMaps({
        lat: event.latLng.lat(),
        lng: event.latLng.lng(),
      });
    } catch (error) {
      console.error('Error handling map click:', error);
    }
  };

  const changeMarkerPosition = (location) => {
    try {
      if (marker.current) marker.current.setMap(null);
      marker.current = new window.google.maps.Marker({
        position: location,
        map: map,
      });
    } catch (error) {
      console.error('Error changing marker position:', error);
    }
  };

  useEffect(() => {
    try {
      if (!map) {
        return;
      }
      if (query && query?.l_latitude != null && query?.l_longitude != null) {
        const position = { lat: Number(query.l_latitude), lng: Number(query.l_longitude) };
        if (!Number.isNaN(position.lat) && !Number.isNaN(position.lng)) {
          changeMarkerPosition(position);
          map.setCenter(position);
          map.panTo(position);
        }
      } else if (
        customerQuery &&
        customerQuery?.l_latitude != null &&
        customerQuery?.l_longitude != null
      ) {
        const position = {
          lat: Number(customerQuery.l_latitude),
          lng: Number(customerQuery.l_longitude),
        };
        if (!Number.isNaN(position.lat) && !Number.isNaN(position.lng)) {
          changeMarkerPosition(position);
          map.setCenter(position);
          map.panTo(position);
        }
      }
    } catch (error) {
      console.error('Error handling query/customerQuery changes:', error);
    }
  }, [query, customerQuery, map]);

  const search = (e) => {
    try {
      const value = e.target.value;
      setSearchValue(value);
      const input = document.getElementById('autocomplete-input');

      // Check if input matches coordinates format with flexible spacing and minus signs
      // Replace en-dash and em-dash with regular minus sign and remove extra spaces
      const normalizedValue = value.trim().replace(/[\u2013\u2014]/g, '-').replace(/\s+/g, '');
      const coordMatch = normalizedValue.match(/^(-?\d+\.?\d*),(-?\d+\.?\d*)$/);

      if (coordMatch && map) {
        const lat = parseFloat(coordMatch[1]);
        const lng = parseFloat(coordMatch[2]);
        const latLng = { lat, lng };

        changeMarkerPosition(latLng);
        map.panTo(latLng);

        setValue('l_latitude', lat);
        setValue('l_longitude', lng);
        setValue('l_address', '');
        setValue('l_city', '');
        setValue('l_state', '');
        setValue('l_zip', '');

        handleMaps({ latLng });
        return;
      }

      // Otherwise, use Google Places Autocomplete
      const autocomplete = new window.google.maps.places.Autocomplete(input);

      autocomplete.addListener('place_changed', () => {
        try {
          const place = autocomplete.getPlace();

          if (!place.geometry || !map) return;

          changeMarkerPosition(place.geometry.location);

          // Pan map to center around marker
          map.panTo(place.geometry.location);

          const dir = place.formatted_address;
          setValue('l_address', dir);
          const cit = checkLongName(place, 2) ? place.address_components[2].long_name : null;
          setValue('l_city', cit);
          const stat = checkLongName(place, 4) ? place.address_components[4].long_name : null;
          setValue('l_state', stat);
          const zip = checkLongName(place, 6) ? place.address_components[6].long_name : null;
          setValue('l_zip', zip);
          setValue('l_latitude', place.geometry.location.lat());
          setValue('l_longitude', place.geometry.location.lng());
          setSearchValue(
            `${place.geometry.location.lat()}, ${place.geometry.location.lng()}`
          );
          handleMaps({
            latLng: { lat: place.geometry.location.lat(), lng: place.geometry.location.lng() },
            dir,
            cit,
            stat,
            zip,
          });
        } catch (error) {
          console.error('Error handling place changed:', error);
        }
      });
    } catch (error) {
      console.error('Error initializing autocomplete or parsing coordinates:', error);
    }
  };

  return (
    <div className='w-500 h-512'>
      <TextField
        id='autocomplete-input'
        label='Search Address or Coordinates (latitude , longitude)'
        variant='outlined'
        fullWidth
        value={searchValue}
        onChange={search}
        InputLabelProps={{ shrink: true }}
      />
      <div ref={mapRef} style={{ width: '550px', height: '500px', margin: '16px 0' }} />
    </div>
  );
};

export default QuarryPitMap;
